import React from 'react';
import LegalPage from '../components/LegalPage';

// Source: WorldVibe Privacy Policy (effective Aug 16, 2026).
// This policy is written to match the Play Console Data Safety form
// word-for-word. When code changes (new field collected, new SDK added),
// update BOTH this file AND the Data Safety declaration in Play Console.
const CONTENT = `
**Effective Date:** August 16, 2026
**Last Updated:** August 16, 2026

## 1. Introduction

Welcome to WorldVibe ("WorldVibe," "we," "our," or "us"). WorldVibe is a global social discovery platform, operated by **SarvaRitam Businesses LLP**, that connects travelers through communities ("Planets"), real-time messaging, feed posts, a daily World Quest, virtual gifts, avatars, pets, ranking systems, and other social experiences.

By accessing or using WorldVibe you agree to this Privacy Policy.

## 2. Scope

This Privacy Policy applies to:
- WorldVibe website (worldvibe.online)
- WorldVibe mobile applications on Google Play and the App Store
- All community, messaging, feed, dating, and gaming features

## 3. Information We Collect

### 3.1 Account Information
- Username
- Display name
- Email address
- Password (bcrypt-hashed, we never see the raw value)
- **Date of birth** (required to verify you are 13 or older — see §15)
- Country / flag
- Language preference
- Profile image (either an avatar you upload or a generated illustration)
- Profile banner (a CSS gradient string or an image you upload)
- Bio and interests you choose to add

### 3.2 Authentication Identifiers (only when you use social login)
- **Google Sign-In**: Google Account ID, email, display name, profile picture URL
- **Sign in with Apple**: Apple Sub identifier, email (or Apple's private relay email if you choose to hide it), first + last name (only on your first authorization)

### 3.3 User-Generated Content
Everything you create on WorldVibe:
- Direct messages (text + image attachments)
- Planet (community) messages
- Feed posts (text + image + likes + comments)
- World Quest responses (text + photo)
- Dating profile
- Pet selections, avatar customizations
- Reactions, gifts sent, comments

### 3.4 Communication & Engagement Data
- Login history and session activity
- Features used, communities joined, games played
- Gifts sent and received
- XP earned, rank progression, daily-quest streak
- Notifications you have read
- Users you follow, block, or report

### 3.5 Device Information (via web browser or mobile shell)
- Device type, operating system, browser type
- IP address
- App version, crash reports
- Time zone

### 3.6 Location Information
We collect **coarse location only** — the country you select on your profile, plus rough location inferred from your IP address for security / spam prevention. **WorldVibe never requests precise (GPS) location.**

### 3.7 Photos You Choose to Share
When you upload a photo (avatar, chat attachment, feed post, quest answer, profile banner) the file is stored on our servers and shown to other travelers where you posted it. We do NOT access your camera roll or gallery in bulk — only the specific file you pick with the system photo picker.

### 3.8 Purchase Information
When you buy Stars we receive:
- **Razorpay** (web): payment token, order id, transaction status. Never full card numbers or CVV.
- **Google Play Billing** (Android): Play purchase token, product id, order id.
- **Apple StoreKit** (iOS): transaction id, product id, receipt.

Payment card details are handled entirely by the payment processor; WorldVibe does not see or store them.

## 4. How We Use Your Information

- **Provide the service** — accounts, profiles, messaging, communities, feed, quests, gifts, ranking
- **Personalize** — suggest planets, users, pets, and dating matches
- **Safety & moderation** — detect spam, abuse, CSAE material, and enforce community guidelines (see §8 and our [Child Safety Standards](/child-safety))
- **Security** — prevent fraud, brute-force attempts, and unauthorised access
- **Communications** — welcome emails, password resets, security alerts, and product updates (all sent via Resend)
- **Improve WorldVibe** — analytics, performance, bug fixes, and product development

## 5. Legal Basis for Processing

Where required by law (GDPR, DPDP Act, etc.) we process personal information based on:
- **Contractual necessity** — to provide the service you asked for
- **Legitimate interests** — service improvement, platform security, fraud prevention
- **Consent** — for optional profile details and marketing communications
- **Legal compliance** — to meet applicable laws, court orders, or regulatory requests

## 6. AI-Powered Features

WorldVibe includes AI-generated content, specifically:
- **Cartoon avatar generation** (Gemini Nano Banana image model, via Emergent LLM integrations)
- **AI planet hosts** — occasional short messages generated by Claude Sonnet
- **Text moderation** — automated systems that scan messages for spam / abuse / CSAE material

When you use these features, the text you provide (avatar prompt, message text being moderated) is sent to the AI provider for processing. AI providers may retain the request briefly for abuse-prevention purposes as described in their own privacy policies. Your responses to daily World Quest are stored on our servers but are NOT sent to AI providers unless flagged for moderation review.

## 7. WorldVibe Virtual Economy

WorldVibe includes Stars (premium currency), Credits, XP, Cosmic Ranks, Pets, Virtual Gifts, and cosmetic items. These are digital licenses, have no inherent monetary value, are non-transferable outside WorldVibe, and may be modified, balanced, discontinued, or updated at our discretion.

## 8. Community Moderation & Reporting

WorldVibe combines human moderation and automated systems to identify spam, harassment, hate speech, CSAE / CSAM, and other violations. Every user can:
- **Report** any user, planet message, DM, feed post, or quest answer via the flag icon inside the app (see our [Child Safety Standards](/child-safety))
- **Block** any user — they can no longer message you, and their feed posts / profile activity are hidden from your view

Actions we may take on violations include removing content, warnings, temporary restrictions, permanent bans, and reporting confirmed illegal content to appropriate authorities where required by law.

## 9. Cookies and Similar Technologies

WorldVibe uses HTTP cookies (\`access_token\`, \`session_token\`) to keep you signed in, plus session storage for preferences. Analytics and security cookies help us remember settings, keep sessions safe, and analyse aggregate usage patterns. You can disable cookies via your browser settings, though the app may stop working correctly.

## 10. Third-Party Services

WorldVibe uses the following third-party service providers. Each processes data under its own privacy policy:

**Authentication**
- Google Sign-In (Google LLC)
- Sign in with Apple (Apple Inc.)

**Payments**
- Razorpay (web checkout, India)
- Google Play Billing (Android in-app purchases)
- Apple StoreKit (iOS in-app purchases)

**Email delivery**
- Resend (transactional emails: welcome, password reset, moderation notices)

**AI**
- Emergent LLM Integrations (routing to Claude Sonnet 4.5 for text, Gemini Nano Banana for avatar generation)

**Hosting & Infrastructure**
- Emergent Cloud (application hosting)
- MongoDB Atlas (managed database)

## 11. Payments

Payments may be processed for Star purchases, VIP subscriptions, and premium features. WorldVibe **never stores** full card numbers, CVV, banking passwords, or UPI PINs — those are handled by the payment provider directly.

## 12. Data Retention

We retain information only as long as reasonably necessary for the purposes for which it was collected:

- **Account data** — while your account remains active
- **Deleted-account audit log** — up to 30 days after deletion (deletion timestamp + user id only, per Play Data Deletion policy)
- **Security logs** — up to 24 months
- **Support tickets** — up to 5 years
- **Financial records** — as required by law
- **Moderation records** — as necessary for platform safety

## 13. Your Rights

Depending on applicable law you may have the right to:
- **Access** — request a copy of your personal information
- **Correction** — correct inaccurate information
- **Deletion** — request account deletion (self-service in-app: Profile → Settings → Delete account, or use the public form at [worldvibe.online/delete-account](/delete-account))
- **Restriction** — limit certain processing activities
- **Portability** — receive a copy of your data
- **Objection** — object to specific processing

Requests: **support@worldvibe.online**

## 14. International Transfers

WorldVibe serves travelers worldwide. Information may be processed in countries different from your country of residence. Where applicable we implement standard safeguards (contractual clauses, encryption in transit) to protect personal information during international transfers.

## 15. Children's Privacy (Age 13+)

**WorldVibe is intended for users aged 13 years and older.** During signup you must provide a birthdate; accounts belonging to travelers under 13 are refused at creation and any under-13 data attempted to be created is deleted immediately. If we later discover an under-13 account we will suspend it, delete associated information, and (where applicable) contact the guardian.

For CSAE / CSAM concerns please see our [Child Safety Standards](/child-safety) — our designated Child Safety Point of Contact is **sarvaritam@gmail.com**.

## 16. Security Measures

WorldVibe employs reasonable technical and organisational measures including:
- Passwords hashed with bcrypt (server never sees plaintext after signup)
- Auth tokens delivered via HTTPS-only, HttpOnly, SameSite=None cookies
- Access controls, rate limiting, brute-force detection
- Automated moderation and human review for CSAE/CSAM
- Regular security reviews

No platform can guarantee absolute security. You are responsible for protecting your credentials and choosing a strong password.

## 17. Account Deletion

You can permanently delete your WorldVibe account at any time:
- **Inside the app**: Profile → Settings → Delete account (requires password or a typed confirmation phrase)
- **Public web form** (no login needed): [worldvibe.online/delete-account](/delete-account) — for users who no longer have the app installed

Upon deletion we hard-delete your account row, feed posts, quest responses, follows, notifications, pets, dating profile, and settings. Messages you sent to other users are anonymised to "[message removed]" so the other party's chat history stays coherent (per Play Data Deletion policy guidance). Backups may retain minimal audit metadata for up to 30 days.

## 18. Changes to This Policy

We may update this Privacy Policy periodically. Material changes will be communicated via in-app notifications and/or email. Continued use of WorldVibe after an update constitutes acceptance of the revised policy.

## 19. Contact Information

- **General support**: support@worldvibe.online
- **Security reports**: security@worldvibe.online
- **Child Safety Point of Contact**: sarvaritam@gmail.com (see [Child Safety Standards](/child-safety))
- **Business & partnerships**: hello@worldvibe.online
- **Data deletion (no login required)**: [worldvibe.online/delete-account](/delete-account)

**SarvaRitam Businesses LLP**
`;

export default function Privacy() {
  return (
    <LegalPage
      title="Privacy Policy"
      tagline="How WorldVibe handles your data"
      accent="#22D3EE"
      content={CONTENT}
      backTo="/"
      testId="privacy-page"
    />
  );
}
